// filepath: apps/web/src/admin/organization-units/edit.tsx
//
// ISS-517 — OrganizationUnitEditScreen.
//
// TWO mutations, TWO OCC tokens, TWO routes (this is forced by the Go
// backend, not a UI choice):
//
//   PATCH /api/directory/organization-units/{id}
//     body {row_version, name?, code?, clear_code?}
//     → name / code only (`handlers.go:110-115`,
//       `repository.go:113-158`).
//
//   POST /api/directory/organization-units/{id}/move
//     body {row_version, parent_organization_unit_id}
//     → reparent only (`handlers.go:151-154`,
//       `repository.go:165-204`).
//
// INV-7: each carries the row_version from the latest GET response.
// A stale submission returns 409 + code='row_version_mismatch'
// (`handlers.go:329-330`) and the SPA surfaces a <StaleVersionError>
// with a Reload action — never a silent overwrite.
//
// BA ruling note 18333 (D2) — cycle detection is SERVER-AUTHORITATIVE.
// The reparent picker below performs NO client-side descendant walk and
// NO client-side cycle test. A server 409 + code='cycle_detected'
// (`handlers.go:331-332`) is mapped to `parentError` and rendered on
// the parent picker. That is the ONLY cycle error the SPA can produce.
//
// ISS-431-uatrzt2: the project opts out of react-router-dom
// (FR-UI-CABINET-1 AC-9); navigation uses the project's own
// `navigate()` primitive.

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { Banner } from "@/design-system/Banner";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import {
  OrganizationUnitForm,
  OrganizationUnitMoveForm,
  type OrganizationUnitCandidate,
  type OrganizationUnitFormValues,
} from "./OrganizationUnitForm";
import {
  getOrganizationUnit,
  listOrganizationUnits,
  moveOrganizationUnit,
  newIdempotencyKey,
  updateOrganizationUnit,
  type UpdateOrganizationUnitInput,
} from "./api";
import type { OrganizationUnit } from "@/features/directory/types";

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

/**
 * INV-7 stale-write detector. Narrowed on BOTH status and code:
 * `cycle_detected` also arrives as 409
 * (`handlers.go:325-338`), and mis-rendering it as a version
 * conflict would tell the operator the wrong thing.
 */
function isRowVersionConflict(e: unknown): boolean {
  return (
    e instanceof ApiError &&
    e.status === 409 &&
    e.code === "row_version_mismatch"
  );
}

/**
 * BA ruling D2 — the server is the cycle authority. Only its 409 +
 * `cycle_detected` envelope produces a parent-picker validation error.
 */
function isCycleDetected(e: unknown): boolean {
  return (
    e instanceof ApiError && e.status === 409 && e.code === "cycle_detected"
  );
}

export interface OrganizationUnitEditScreenProps {
  id?: string;
  loader?: (id: string) => Promise<OrganizationUnit>;
  submit?: (
    id: string,
    input: UpdateOrganizationUnitInput,
    key: string,
  ) => Promise<void>;
  mover?: (
    id: string,
    nextParentId: string | null,
    rowVersion: number,
    key: string,
  ) => Promise<void>;
  /** Test override for the reparent picker feed. */
  loadUnits?: () => Promise<OrganizationUnitCandidate[]>;
}

export function OrganizationUnitEditScreen({
  id = "",
  loader,
  submit,
  mover,
  loadUnits,
}: OrganizationUnitEditScreenProps = {}): React.ReactElement {
  const { t } = useTranslation();
  const [row, setRow] = useState<OrganizationUnit | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [moving, setMoving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [parentError, setParentError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  const [stale, setStale] = useState<boolean>(false);
  const [moved, setMoved] = useState<boolean>(false);
  const [parents, setParents] = useState<OrganizationUnitCandidate[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setPermissionDenied(false);
    setStale(false);
    try {
      const l = loader ?? ((pid: string) => getOrganizationUnit(pid));
      setRow(await l(id));
    } catch (e) {
      if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [id, loader]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    const unitLoader =
      loadUnits ?? (() => listOrganizationUnits({ include_closed: true }));
    void unitLoader()
      .then((units) => {
        if (!cancelled) setParents(units);
      })
      .catch(() => {
        // A failed picker load degrades to an empty option list; the
        // reparent request is still correct (it sends an explicit
        // parent_organization_unit_id or null).
        if (!cancelled) setParents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [loadUnits]);

  const onSubmit = useCallback(
    async (values: OrganizationUnitFormValues) => {
      if (!row) return;
      setSubmitting(true);
      setError(null);
      setNameError(null);
      setStale(false);
      try {
        const key = newIdempotencyKey();
        const trimmedCode = values.code.trim();
        const input: UpdateOrganizationUnitInput = {
          name: values.name,
          // Go `Validate` rejects a present-but-blank code
          // (`package.go:79-82`), so clearing the field MUST travel
          // as `clear_code: true` — never as `code: ""`.
          ...(trimmedCode === ""
            ? { clear_code: true }
            : { code: trimmedCode }),
          row_version: row.row_version,
        };
        const submitFn = submit ?? updateOrganizationUnit;
        await submitFn(row.id, input, key);
        navigate(`/admin/organization-units/${encodeURIComponent(row.id)}`);
      } catch (e) {
        if (isPermissionDenied(e)) {
          setPermissionDenied(true);
        } else if (isRowVersionConflict(e)) {
          setStale(true);
        } else if (
          e instanceof ApiError &&
          e.status === 400 &&
          e.code === "invalid_input"
        ) {
          setNameError(
            t(
              "directory.organization_units.edit.invalid_input",
              "The server rejected these values. Check the name and code and try again.",
            ),
          );
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [row, submit, t],
  );

  const onMove = useCallback(
    async (nextParentId: string | null) => {
      if (!row) return;
      setMoving(true);
      setError(null);
      setStale(false);
      setParentError(null);
      try {
        const key = newIdempotencyKey();
        if (mover) {
          await mover(row.id, nextParentId, row.row_version, key);
        } else {
          await moveOrganizationUnit(
            row.id,
            {
              parent_organization_unit_id: nextParentId,
              row_version: row.row_version,
            },
            key,
          );
        }
        setMoved(true);
        await load();
      } catch (e) {
        if (isPermissionDenied(e)) {
          setPermissionDenied(true);
        } else if (isCycleDetected(e)) {
          // BA ruling D2 — the server's recursive CTE
          // (`repository.go:165-203`) is the ONLY cycle authority.
          // The message is the server's, not one computed here.
          setParentError(
            t(
              "directory.organization_units.move.parent_cycle",
              "Reparent would introduce a cycle.",
            ),
          );
        } else if (isRowVersionConflict(e)) {
          setStale(true);
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        setMoving(false);
      }
    },
    [row, mover, load, t],
  );

  if (loading) {
    return (
      <p data-testid="organization-unit-edit-loading">
        {t(
          "directory.organization_units.edit.loading",
          "Loading organization unit…",
        )}
      </p>
    );
  }
  if (permissionDenied) {
    return (
      <div data-testid="organization-unit-edit-permission-denied">
        <PermissionDenied />
      </div>
    );
  }
  if (error && !row) {
    return (
      <p role="alert" data-testid="organization-unit-edit-error">
        {t("directory.organization_units.edit.error", "Error: {{message}}", {
          message: error,
        })}
      </p>
    );
  }
  if (!row) {
    return (
      <p data-testid="organization-unit-edit-not-found">
        {t(
          "directory.organization_units.edit.not_found",
          "Organization unit not found",
        )}
      </p>
    );
  }

  return (
    <section data-testid="organization-unit-edit-screen">
      {stale ? (
        <StaleVersionError
          entityName={row.name}
          onReload={() => void load()}
          data-testid="organization-unit-edit-stale"
        />
      ) : null}

      <OrganizationUnitForm
        // ISS-805 — the form tracks each field's dirty state, so pointing
        // the route at a different unit must NOT carry this unit's
        // in-progress edits into the next form. `key` remounts the form on a
        // row change, which is the form's "different record" path (it takes
        // no id prop). A reload of the SAME row keeps the key, so a 409
        // row_version conflict still leaves the operator's text in place.
        key={row.id}
        mode="edit"
        heading={t(
          "directory.organization_units.edit.title",
          "Edit organization unit",
        )}
        initial={{
          // The edit route cannot touch legal_entity_id or the parent
          // (the Go update request has neither field), so they are
          // pinned to the loaded record and never sent.
          legal_entity_id: row.legal_entity_id,
          parent_organization_unit_id: row.parent_organization_unit_id,
          name: row.name,
          code: row.code ?? "",
        }}
        onSubmit={onSubmit}
        onCancel={() =>
          navigate(`/admin/organization-units/${encodeURIComponent(row.id)}`)
        }
        submitting={submitting}
        error={error}
        nameError={nameError}
        submitLabel={t("directory.organization_units.edit.submit", "Save")}
        loadingLabel={t(
          "directory.organization_units.edit.submitting",
          "Saving…",
        )}
      />

      {row.status === "active" ? (
        <div className="organization-unit-edit-move-section">
          <h2>{t("directory.organization_units.move.title", "Move unit")}</h2>
          {moved ? (
            // design-adherence-ignore: <Banner> test hook via wrapper.
            <span data-testid="organization-unit-edit-moved">
              <Banner tone="info">
                {t(
                  "directory.organization_units.move.moved",
                  "Organization unit moved.",
                )}
              </Banner>
            </span>
          ) : null}
          <OrganizationUnitMoveForm
            currentParentId={row.parent_organization_unit_id}
            parentCandidates={parents}
            onMove={onMove}
            moving={moving}
            parentError={parentError}
          />
        </div>
      ) : null}
    </section>
  );
}

export default OrganizationUnitEditScreen;
